import type { Landmark, TrailEvent } from "./types";

// The trail runs from the old fiat world toward Freedom City.
// Landmarks are BCH-flavored waypoints.
export const LANDMARKS: Landmark[] = [
  {
    name: "Genesis Station",
    distance: 0,
    description:
      "The trailhead. Wagons loaded with hardware wallets and dreams of peer-to-peer cash. Your journey to Freedom City begins.",
    hasTrader: true,
    biome: "plains",
  },
  {
    name: "Mempool Crossing",
    distance: 220,
    description:
      "A rushing river of unconfirmed transactions. Low fees mean the ferryman lets folks across quickly here.",
    hasTrader: false,
    biome: "river",
  },
  {
    name: "Fort HODL",
    distance: 480,
    description:
      "A weathered outpost where diamond-handed pioneers resupply. Merchants gladly accept BCH — no chargebacks.",
    hasTrader: true,
    biome: "fort",
  },
  {
    name: "Difficulty Ridge",
    distance: 760,
    description:
      "A steep mountain pass. The higher you climb, the harder the trail adjusts against you.",
    hasTrader: false,
    biome: "mountains",
  },
  {
    name: "Merchants' Bazaar",
    distance: 1050,
    description:
      "A bustling market where everyone runs a point-of-sale. Instant, near-free BCH payments fly across the stalls.",
    hasTrader: true,
    biome: "market",
  },
  {
    name: "Custodian's Gulch",
    distance: 1380,
    description:
      "A tempting valley of 'not your keys' banks. Many wagons vanished here. Keep your seed phrase close.",
    hasTrader: false,
    biome: "canyon",
  },
  {
    name: "Freedom City",
    distance: 1700,
    description:
      "The shining destination — a circular economy running entirely on peer-to-peer electronic cash. You made it.",
    hasTrader: true,
    biome: "city",
  },
];

export const TOTAL_DISTANCE = LANDMARKS[LANDMARKS.length - 1].distance;

// Trader price list (in sats per unit)
export const TRADE_PRICES = {
  food: 40,
  medicine: 300,
  gear: 180,
} as const;

// Difficulty tuning. Higher difficulty = fewer starting resources, more
// hazards, harsher damage, but a bigger score multiplier as a reward.
export interface DifficultyConfig {
  id: import("./types").Difficulty;
  label: string;
  blurb: string;
  emoji: string;
  startingInventory: { sats: number; food: number; medicine: number; gear: number };
  foodCostMult: number; // multiplies rations food consumption
  damageMult: number; // multiplies health damage from pace/rations/events
  eventChance: number; // chance a step triggers a random event
  ailmentChance: number; // chance a step inflicts an ailment
  priceMult: number; // multiplies trader buy prices
  scoreMult: number; // multiplies final score
}

export const DIFFICULTIES: Record<import("./types").Difficulty, DifficultyConfig> = {
  banker: {
    id: "banker",
    label: "Banker",
    blurb: "Bailed out at every turn. A gentle stroll to Freedom City.",
    emoji: "🏦",
    startingInventory: { sats: 14000, food: 45, medicine: 5, gear: 6 },
    foodCostMult: 0.8,
    damageMult: 0.7,
    eventChance: 0.45,
    ailmentChance: 0.08,
    priceMult: 0.85,
    scoreMult: 0.75,
  },
  pleb: {
    id: "pleb",
    label: "Pleb",
    blurb: "The honest journey. Stack sats, stay humble, survive.",
    emoji: "⚡",
    startingInventory: { sats: 8000, food: 30, medicine: 3, gear: 4 },
    foodCostMult: 1,
    damageMult: 1,
    eventChance: 0.55,
    ailmentChance: 0.12,
    priceMult: 1,
    scoreMult: 1,
  },
  "no-coiner": {
    id: "no-coiner",
    label: "No-coiner",
    blurb: "Broke, doubtful, and starting from nothing. Brutal.",
    emoji: "💸",
    startingInventory: { sats: 3500, food: 18, medicine: 1, gear: 2 },
    foodCostMult: 1.25,
    damageMult: 1.4,
    eventChance: 0.68,
    ailmentChance: 0.18,
    priceMult: 1.2,
    scoreMult: 1.6,
  },
};

export const AILMENTS = [
  "FOMO fever",
  "paper hands",
  "a nasty case of dysentery",
  "keyboard cramp",
  "screen-glow blindness",
  "moon-madness",
];

function pick<T>(arr: T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)];
}

export const TRAIL_EVENTS: TrailEvent[] = [
  {
    id: "airdrop",
    title: "Surprise Airdrop!",
    emoji: "🎁",
    description:
      "A wandering developer hands your party a stack of freshly minted BCH for testing their new wallet. Fortune smiles.",
    weight: 8,
    choices: [
      {
        label: "Gratefully accept the sats",
        resolve: () => ({
          patch: { sats: 1200 },
          message: "You pocket 1,200 sats. Every little bit stacks up.",
          tone: "good",
        }),
      },
    ],
  },
  {
    id: "toll",
    title: "Fee Market Toll",
    emoji: "🛤️",
    description:
      "A bridge operator demands payment to cross. On BCH the fees are tiny, but this old-timer insists on a premium.",
    weight: 9,
    choices: [
      {
        label: "Pay the toll (400 sats)",
        resolve: () => ({
          patch: { sats: -400 },
          message: "You pay 400 sats and cross without trouble.",
          tone: "info",
        }),
      },
      {
        label: "Ford the river instead",
        resolve: (s) => {
          const bad = (s.seed % 10) < 4;
          return bad
            ? {
                patch: { food: -3, gear: -1 },
                partyDamage: 8,
                message:
                  "The crossing goes wrong! You lose supplies and someone takes a spill.",
                tone: "bad",
              }
            : {
                patch: {},
                message: "You ford the river cleanly and save your sats.",
                tone: "good",
              };
        },
      },
    ],
  },
  {
    id: "scammer",
    title: "A Slick Stranger",
    emoji: "🕴️",
    description:
      "A stranger promises to DOUBLE your BCH if you send him some first. 'Trust me, it's guaranteed,' he grins.",
    weight: 9,
    choices: [
      {
        label: "Send him 1,000 sats",
        resolve: () => ({
          patch: { sats: -1000 },
          message:
            "He rides off into the sunset. Rule #1: if it sounds too good to be true...",
          tone: "bad",
        }),
      },
      {
        label: "Politely decline",
        resolve: () => ({
          patch: {},
          message: "You keep your keys and your composure. Wise traveler.",
          tone: "good",
        }),
      },
    ],
  },
  {
    id: "merchant",
    title: "Roadside Merchant",
    emoji: "🧺",
    description:
      "A friendly vendor with a BCH QR code sells fresh supplies at a fair price — a real circular economy in action.",
    weight: 8,
    choices: [
      {
        label: "Buy food (500 sats → +8 food)",
        resolve: (s) =>
          s.inventory.sats >= 500
            ? {
                patch: { sats: -500, food: 8 },
                message: "A quick tap-to-pay and your wagon is stocked.",
                tone: "good",
              }
            : {
                patch: {},
                message: "You don't have enough sats. The merchant shrugs.",
                tone: "bad",
              },
      },
      {
        label: "Just wave and keep moving",
        resolve: () => ({
          patch: {},
          message: "You press on down the trail.",
          tone: "info",
        }),
      },
    ],
  },
  {
    id: "storm",
    title: "Volatility Storm",
    emoji: "⛈️",
    description:
      "The skies turn red then green then red again. A brutal storm of market volatility batters your wagon.",
    weight: 7,
    choices: [
      {
        label: "Hunker down and wait it out",
        resolve: () => ({
          patch: { food: -4 },
          message:
            "You lose a day and some food, but the wagon holds. This too shall pass.",
          tone: "info",
        }),
      },
      {
        label: "Push through the storm",
        resolve: (s) => {
          const bad = (s.seed % 10) < 6;
          return bad
            ? {
                patch: { gear: -2 },
                partyDamage: 12,
                message: "The storm mauls your party. Gear breaks, spirits sink.",
                tone: "bad",
              }
            : {
                patch: {},
                message: "You barrel through and make great time. Diamond hands!",
                tone: "good",
              };
        },
      },
    ],
  },
  {
    id: "illness",
    title: "Someone Falls Ill",
    emoji: "🤒",
    description:
      "A member of your party wakes up sick from too many sleepless nights watching charts.",
    weight: 8,
    canFire: (s) => s.party.some((p) => p.alive && !p.ailment),
    choices: [
      {
        label: "Use a med kit",
        resolve: (s) =>
          s.inventory.medicine > 0
            ? {
                patch: { medicine: -1 },
                message: "The med kit does the trick. Back on your feet!",
                tone: "good",
              }
            : {
                patch: {},
                partyDamage: 15,
                message:
                  "No medicine left! The illness spreads through the party.",
                tone: "bad",
              },
      },
      {
        label: "Tough it out",
        resolve: () => ({
          patch: {},
          partyDamage: 18,
          message: "You save the medicine, but everyone suffers for it.",
          tone: "bad",
        }),
      },
    ],
  },
  {
    id: "wallet",
    title: "Lost Seed Phrase!",
    emoji: "📝",
    description:
      "The wind snatches a scrap of paper from your wagon. Was that... your backup seed phrase?",
    weight: 5,
    choices: [
      {
        label: "Chase it down",
        resolve: (s) => {
          const good = (s.seed % 10) < 7;
          return good
            ? {
                patch: {},
                message: "You catch it just in time. Never lose your keys!",
                tone: "good",
              }
            : {
                patch: { sats: -800 },
                message:
                  "Too late — a stranger found it first. 800 sats gone.",
                tone: "bad",
              };
        },
      },
    ],
  },
  {
    id: "goodsamaritan",
    title: "Kind Node Runner",
    emoji: "🤝",
    description:
      "A node runner shares her campfire and hot food with your weary party. No charge — just good vibes and P2P spirit.",
    weight: 6,
    choices: [
      {
        label: "Share stories by the fire",
        resolve: () => ({
          patch: { food: 4 },
          partyDamage: -10,
          message: "Bellies full and morale high. Thank you, stranger!",
          tone: "good",
        }),
      },
    ],
  },
  {
    id: "bandits",
    title: "Highway Bandits",
    emoji: "🏴‍☠️",
    description:
      "Masked riders block the trail. 'Hand over your coins!' But your BCH is secured behind a passphrase they'll never crack.",
    weight: 6,
    choices: [
      {
        label: "Offer some food to appease them",
        resolve: () => ({
          patch: { food: -6 },
          message: "They grab the grub and ride off. Your keys stay safe.",
          tone: "info",
        }),
      },
      {
        label: "Refuse and stand your ground",
        resolve: (s) => {
          const bad = (s.seed % 10) < 5;
          return bad
            ? {
                patch: { gear: -1 },
                partyDamage: 14,
                message: "A scuffle breaks out. You escape, but bruised.",
                tone: "bad",
              }
            : {
                patch: {},
                message: "You bluff them cold. They ride off empty-handed.",
                tone: "good",
              };
        },
      },
    ],
  },
  {
    id: "brokenwagon",
    title: "Broken Wagon Wheel",
    emoji: "🛞",
    description:
      "A wheel splinters on a rough patch of trail. You'll need spare gear to repair it.",
    weight: 7,
    choices: [
      {
        label: "Repair with gear",
        resolve: (s) =>
          s.inventory.gear > 0
            ? {
                patch: { gear: -1 },
                message: "Repaired! You roll on down the trail.",
                tone: "good",
              }
            : {
                patch: { food: -3 },
                partyDamage: 6,
                message:
                  "No gear! You lash it together and limp along, losing time and food.",
                tone: "bad",
              },
      },
    ],
  },
];

export function randomAilment(rng: () => number): string {
  return pick(AILMENTS, rng);
}
